import { useEffect, useState, useCallback, useRef } from "react";
import FundScheduleSelectMulti from "app/modules/shared/selectComponents/fundScheduleSelect/FundScheduleSelectMulti";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  acceptAnticipatedExpenditureReport,
  emailAnticipatedExpenditureReport,
  previewAnticipatedExpenditureReport,
  printAnticipatedExpenditureReport,
  resetAnticipatedExpenditureProcess,
  resetData,
  resumeAnticipatedExpenditureProcess,
} from "./AnticipatedExpenditureSlice";
import { AnticipatedExpenditureSelectionModel } from "services/api/cplapi/CPLAPI";
import { toast } from "react-toastify";
import CPLTextEditor from "app/modules/shared/cplTextEditor/CPLTextEditor";
import DevBlockClientFilter from "app/modules/shared/devBlockClientFilter/DevBlockClientFilter";
import { formatDate } from "cpl-react-common/Helpers/StringHelpers";

const AnticipatedExpenditure: React.FC = () => {
  const thunkDispatch = useThunkDispatch();
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(new Date());
  const [selectedDevelopmentIDs, setSelectedDevelopmentIDs] = useState<string[] | undefined>(undefined);
  const [selectedBlockIDs, setSelectedBlockIDs] = useState<string[] | undefined>(undefined);
  const [selectedClientIDs, setSelectedClientIDs] = useState<string[] | undefined>(undefined);
  const [selectedFundScheduleIDs, setSelectedFundScheduleIDs] = useState<number[] | undefined>(undefined);
  const [coverLetter, setCoverLetter] = useState<string>("");
  const [includeCoverLetter, setIncludeCoverLetter] = useState<boolean>(false);
  const [formKey, setFormKey] = useState(0);

  const initialValuesRef = useRef<{
    developmentIDs?: string[];
    blockIDs?: string[];
    clientIDs?: string[];
    fundScheduleIDs?: number[];
  }>({});

  const previousFormKey = useRef(formKey);
  const [useSavedFundSchedules, setUseSavedFundSchedules] = useState(false);
  const isInitialMount = useRef(true);

  const [hasPreviewed, setHasPreviewed] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [hasPrinted, setHasPrinted] = useState(false);
  const [hasEmailed, setHasEmailed] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);

  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const hasDevelopmentSelected = selectedDevelopmentIDs && selectedDevelopmentIDs.length > 0;
  const isDateRangeValid = fromDate && toDate && fromDate < toDate;
  const canPreview =
    isDateRangeValid &&
    hasDevelopmentSelected &&
    (selectedFundScheduleIDs?.length ?? 0) > 0 &&
    (selectedClientIDs?.length ?? 0) > 0;

  useEffect(() => {
    const checkResumableProcess = async () => {
      try {
        const response = await thunkDispatch(resumeAnticipatedExpenditureProcess()).unwrap();
        const hasData = response && response.hasAccepted;

        if (response?.isBackgroundProcessRunning) {
          setIsAccepting(true);
          setStatusMessage("Your reports are being processed. Please wait...");
          setIsLoading(false);
          return;
        }

        if (hasData) {
          initialValuesRef.current = {
            developmentIDs: response.developmentIDs,
            blockIDs: response.blockIDs,
            clientIDs: response.clientIDs,
            fundScheduleIDs: response.fundScheduleIDs,
          };

          if (response.fromDate) setFromDate(new Date(response.fromDate));
          if (response.toDate) setToDate(new Date(response.toDate));
          if (response.developmentIDs) setSelectedDevelopmentIDs(response.developmentIDs);
          if (response.blockIDs) setSelectedBlockIDs(response.blockIDs);
          if (response.clientIDs) setSelectedClientIDs(response.clientIDs);
          if (response.fundScheduleIDs) {
            setSelectedFundScheduleIDs(response.fundScheduleIDs);
            setUseSavedFundSchedules(true);
            isInitialMount.current = true;
          }
          if (response.includeCoverLetter) setIncludeCoverLetter(response.includeCoverLetter);
          if (response.coverLetter) setCoverLetter(response.coverLetter);

          setHasPreviewed(true);
          setHasAccepted(true);

          if (response.hasPrinted) setHasPrinted(true);
          if (response.hasEmailed) setHasEmailed(true);
        } else {
          const now = new Date();
          setFromDate(new Date(now.getFullYear(), now.getMonth(), 1));
          setToDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
        }
      } catch {
        const now = new Date();
        setFromDate(new Date(now.getFullYear(), now.getMonth(), 1));
        setToDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
      } finally {
        setIsLoading(false);
      }
    };
    checkResumableProcess();
  }, [thunkDispatch]);

  useEffect(() => {
    if (isInitialMount.current && useSavedFundSchedules) {
      setUseSavedFundSchedules(false);
      isInitialMount.current = false;
    }
  }, [useSavedFundSchedules]);

  useEffect(() => {
    if (previousFormKey.current !== formKey) {
      setSelectedFundScheduleIDs(undefined);
      setUseSavedFundSchedules(false);
      previousFormKey.current = formKey;
    }
  }, [formKey]);

  const buildSelectionModel = useCallback(
    () =>
      new AnticipatedExpenditureSelectionModel({
        fromDate,
        toDate,
        developmentIDs: selectedDevelopmentIDs,
        fundScheduleIDs: selectedFundScheduleIDs,
        blockIDs: selectedBlockIDs,
        clientIDs: selectedClientIDs,
        includeCoverLetter,
        coverLetter,
      }),
    [
      fromDate,
      toDate,
      selectedDevelopmentIDs,
      selectedFundScheduleIDs,
      selectedBlockIDs,
      selectedClientIDs,
      includeCoverLetter,
      coverLetter,
    ]
  );

  async function handlePreviewReport() {
    setIsGenerating(true);
    setStatusMessage("Generating Anticipated Expenditure Report Previews");

    try {
      const response = await thunkDispatch(
        previewAnticipatedExpenditureReport({
          reportSelections: buildSelectionModel(),
        })
      ).unwrap();

      if (response.previewURL != null) {
        window.open(response.previewURL, "_blank");
        setHasPreviewed(true);
      }

      setStatusMessage(null);
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleAcceptReports() {
    setIsAccepting(true);
    setStatusMessage("Accepting Reports");
    try {
      await thunkDispatch(acceptAnticipatedExpenditureReport({ reportSelections: buildSelectionModel() })).unwrap();

      setHasAccepted(true);
      setStatusMessage(null);
    } finally {
      setIsAccepting(false);
    }
  }

  async function handlePrintReports() {
    setIsPrinting(true);
    setStatusMessage("Anticipated Expenditure Reports are being sent to print");
    try {
      const response = await thunkDispatch(
        printAnticipatedExpenditureReport({
          reportSelections: buildSelectionModel(),
        })
      ).unwrap();

      if (response.previewURL != null) {
        window.open(response.previewURL, "_blank");
      }
      if (response.message != null) {
        toast.success(response.message);
      }

      setHasPrinted(true);
      setStatusMessage(null);
    } finally {
      setIsPrinting(false);
    }
  }

  async function handleEmailReports() {
    setIsEmailing(true);
    setStatusMessage("Anticipated Expenditure Report Emails are being sent.");
    try {
      const response = await thunkDispatch(
        emailAnticipatedExpenditureReport({
          reportSelections: buildSelectionModel(),
        })
      ).unwrap();

      if (response.message != null) {
        toast.success(response.message);
      }

      setHasEmailed(true);
      setStatusMessage(null);
    } finally {
      setIsEmailing(false);
    }
  }

  const resetPage = useCallback(async () => {
    await thunkDispatch(resetAnticipatedExpenditureProcess());

    initialValuesRef.current = {};
    thunkDispatch(resetData());

    setSelectedDevelopmentIDs(undefined);
    setSelectedBlockIDs(undefined);
    setSelectedClientIDs(undefined);
    setSelectedFundScheduleIDs(undefined);
    setUseSavedFundSchedules(false);
    setIncludeCoverLetter(false);
    setCoverLetter("");

    setHasPreviewed(false);
    setHasAccepted(false);
    setHasPrinted(false);
    setHasEmailed(false);
    setStatusMessage(null);
    const now = new Date();
    setFromDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setToDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    setFormKey((prev) => prev + 1);
  }, [thunkDispatch]);

  if (isLoading) {
    return (
      <div className="card d-inline-flex mb-6">
        <div className="card-header">
          <div className="card-title">Anticipated Expenditure Report</div>
        </div>
        <div className="card-body">
          <div className="d-flex align-items-center gap-2">
            <div className="spinner-border text-primary" role="status" />
            <span>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card d-inline-flex mb-6">
      <div className="card-header">
        <div className="card-title">Anticipated Expenditure Report</div>
      </div>
      <div className="card-body">
        <div className="mb-5">Please select your required report parameters.</div>
        <div className="d-flex gap-5">
          <div className="d-flex flex-column gap-2">
            <div>Fund Schedule Billing Period</div>
            <div className="d-flex justify-content-between align-items-center">
              <label className="me-2">From</label>
              <div>
                <input
                  data-test-id="anticipated-expenditure-page__from-date-input"
                  type="date"
                  className="form-control form-control-solid"
                  defaultValue={formatDate(fromDate)}
                  onChange={(event) => {
                    setFromDate(new Date(event.target.value));
                  }}
                ></input>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <label className="me-2">To</label>
              <div>
                <input
                  data-test-id="anticipated-expenditure-page__to-date-input"
                  type="date"
                  className="form-control form-control-solid"
                  defaultValue={formatDate(toDate)}
                  onChange={(event) => {
                    setToDate(new Date(event.target.value));
                  }}
                ></input>
              </div>
            </div>
          </div>
          <DevBlockClientFilter
            key={`filter-${formKey}`}
            formKey={formKey}
            onDevelopmentChange={setSelectedDevelopmentIDs}
            onBlockChange={setSelectedBlockIDs}
            onClientChange={setSelectedClientIDs}
            className="mb-4"
            serviceChargeOnly={true}
            defaultDevelopmentIDs={initialValuesRef.current.developmentIDs}
            defaultBlockIDs={initialValuesRef.current.blockIDs}
            defaultClientIDs={initialValuesRef.current.clientIDs}
          />
          <div className="d-flex flex-column gap-2">
            <label>Fund Schedule(s)</label>
            <FundScheduleSelectMulti
              data-test-id="anticipated-expenditure-page__fund-schedules-select"
              key={`fund-${formKey}`}
              defaultAll={false}
              handleChange={(selection: number[]) => {
                setSelectedFundScheduleIDs(selection);
              }}
              developmentIDs={selectedDevelopmentIDs}
              disabled={!hasDevelopmentSelected}
              isServiceChargeOnly={true}
              defaultSome={useSavedFundSchedules ? initialValuesRef.current.fundScheduleIDs : undefined}
            />
          </div>
        </div>

        <div className="d-flex flex-row gap-2 my-5">
          <input
            data-test-id="anticipated-expenditure-page__include-cover-letter-checkbox"
            className="form-check-input"
            type="checkbox"
            checked={includeCoverLetter}
            onChange={() => setIncludeCoverLetter(!includeCoverLetter)}
          />
          <label>Include Cover Letter</label>
        </div>
        <div className={`${includeCoverLetter ? "d-flex" : "d-none"}`}>
          <CPLTextEditor
            data-test-id="anticipated-expenditure-page__cover-letter-text-editor"
            value={coverLetter}
            onChange={(value) => {
              setCoverLetter(value);
            }}
            disabled={!includeCoverLetter}
            height={200}
            allowImages={false}
            enableAutoComplete={false}
          />
        </div>

        <div className="d-flex justify-content-between my-5">
          <div className="d-flex gap-3">
            <button
              data-test-id="anticipated-expenditure-page__preview-reports-button"
              className="btn btn-primary btn-sm"
              disabled={!canPreview || isGenerating}
              onClick={handlePreviewReport}
            >
              Preview Reports
            </button>

            {hasPreviewed && (
              <button
                data-test-id="anticipated-expenditure-page__accept-reports-button"
                className="btn btn-primary btn-sm"
                disabled={isAccepting || hasAccepted}
                onClick={handleAcceptReports}
              >
                Accept Reports
              </button>
            )}

            {hasAccepted && (
              <>
                <button
                  data-test-id="anticipated-expenditure-page__print-reports-button"
                  className="btn btn-primary btn-sm"
                  disabled={isPrinting || hasPrinted}
                  onClick={handlePrintReports}
                >
                  Print Reports
                </button>
                <button
                  data-test-id="anticipated-expenditure-page__email-reports-button"
                  className="btn btn-primary btn-sm"
                  disabled={isEmailing || hasEmailed}
                  onClick={handleEmailReports}
                >
                  Email Reports
                </button>
              </>
            )}
          </div>

          <button
            data-test-id="anticipated-expenditure-page__reset-button"
            className="btn btn-secondary btn-sm"
            onClick={resetPage}
          >
            Reset
          </button>
        </div>

        {(isGenerating || isPrinting || isAccepting || isEmailing) && (
          <div className="d-flex align-items-center gap-2 mt-5">
            <div className="spinner-border text-primary" role="status" />
            <span>{statusMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnticipatedExpenditure;
